/**
 * Subject-choice rule evaluation in the browser, for instant feedback while ticking.
 *
 * ⚠ This is a line-for-line copy of `Capabilities/Assessment/Enrollment/SubjectChoiceRuleEvaluator.cs`, and the
 * server stays authoritative: it evaluates again on every read and rejects a save that breaks a rule. Both copies
 * are held to `ScadsysAssessment.Tests/Fixtures/subject-choice-rules.json`, which the C# tests run. There is no JS
 * test runner, so when you change either evaluator, change the fixture with it and keep messages identical.
 */

export interface RuleDefinition {
    id: string;
    name: string;
    appliesToAllSubjects: boolean;
    measure: string;
    minimum?: number | null;
    maximum?: number | null;
    moduleIDs: string[];
}

export interface OfferedSubject {
    moduleID: string;
    name: string;
    credits?: number | null;
}

export interface Corequisite {
    moduleID: string;
    requiredModuleID: string;
}

export interface Offering {
    rules: RuleDefinition[];
    subjects: OfferedSubject[];
    corequisites: Corequisite[];
}

export const FindingKinds = {
    Rule: "Rule",
    Corequisite: "Corequisite",
    NotOffered: "NotOffered",
    Unsatisfiable: "Unsatisfiable",
    MissingCredits: "MissingCredits",
} as const;

export interface Finding {
    kind: string;
    ruleID?: string | null;
    message: string;
}

const has = (value: number | null | undefined): value is number => value !== null && value !== undefined;

const isCredits = (rule: RuleDefinition) => rule.measure?.toLowerCase() === "credits";

/** The rule's offered members, in the order the grade lists its subjects. */
const members = (rule: RuleDefinition, subjects: OfferedSubject[]) =>
    subjects.map((s) => s.moduleID).filter((id) => rule.appliesToAllSubjects || (rule.moduleIDs ?? []).includes(id));

/** Matches C#'s `decimal.ToString("0.##")`. */
const number = (value: number) => String(Math.round(value * 100) / 100);

const join = (names: string[]) =>
    names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

export const bounds = (minimum?: number | null, maximum?: number | null) => {
    if (has(minimum) && has(maximum)) return minimum === maximum ? `exactly ${number(minimum)}` : `between ${number(minimum)} and ${number(maximum)}`;
    if (has(minimum)) return `at least ${number(minimum)}`;
    if (has(maximum)) return `at most ${number(maximum)}`;
    return "any number";
};

export function gradeFindings(offering: Offering): Finding[] {
    const findings: Finding[] = [];
    if (offering.subjects.length === 0) return findings;
    const subjects = new Map(offering.subjects.map((s) => [s.moduleID, s]));

    for (const rule of offering.rules) {
        const ids = members(rule, offering.subjects);
        const credits = isCredits(rule);
        if (!rule.appliesToAllSubjects && ids.length === 0) {
            findings.push({
                kind: FindingKinds.NotOffered,
                ruleID: rule.id,
                message: `${rule.name}: none of its subjects is offered in this grade, so it is not checked.`,
            });
            continue;
        }

        const reach = credits ? ids.reduce((sum, id) => sum + (subjects.get(id)!.credits ?? 0), 0) : ids.length;
        if (has(rule.minimum) && has(rule.maximum) && rule.minimum > rule.maximum)
            findings.push({
                kind: FindingKinds.Unsatisfiable,
                ruleID: rule.id,
                message: `${rule.name}: the minimum is more than the maximum, so it is not checked.`,
            });
        else if (has(rule.minimum) && rule.minimum > reach)
            findings.push({
                kind: FindingKinds.Unsatisfiable,
                ruleID: rule.id,
                message:
                    `${rule.name}: needs ${number(rule.minimum)}${credits ? " credits" : ""} but this grade offers only ` +
                    `${number(reach)}${credits ? " credits" : ""}, so it is not checked.`,
            });

        if (credits) {
            const missing = ids.filter((id) => !has(subjects.get(id)!.credits)).map((id) => subjects.get(id)!.name);
            if (missing.length > 0)
                findings.push({
                    kind: FindingKinds.MissingCredits,
                    ruleID: rule.id,
                    message: `${rule.name}: ${join(missing)} ${missing.length === 1 ? "has no credits and counts" : "have no credits and count"} as 0.`,
                });
        }
    }
    return findings;
}

/** The rules a student is checked against: everything except rules the grade cannot apply this year. */
export function activeRules(offering: Offering): RuleDefinition[] {
    const skipped = new Set(
        gradeFindings(offering)
            .filter((f) => f.kind === FindingKinds.NotOffered || f.kind === FindingKinds.Unsatisfiable)
            .map((f) => f.ruleID)
    );
    return offering.rules.filter((r) => !skipped.has(r.id));
}

export function studentFindings(offering: Offering, chosenModuleIDs: string[]): Finding[] {
    const findings: Finding[] = [];
    if (offering.subjects.length === 0) return findings;
    const subjects = new Map(offering.subjects.map((s) => [s.moduleID, s]));
    const chosen = new Set(chosenModuleIDs.filter((id) => subjects.has(id)));

    for (const rule of activeRules(offering)) {
        const ids = members(rule, offering.subjects);
        const taken = ids.filter((id) => chosen.has(id));
        const credits = isCredits(rule);
        const value = credits ? taken.reduce((sum, id) => sum + (subjects.get(id)!.credits ?? 0), 0) : taken.length;
        const below = has(rule.minimum) && value < rule.minimum;
        const above = has(rule.maximum) && value > rule.maximum;
        if (!below && !above) continue;
        findings.push({ kind: FindingKinds.Rule, ruleID: rule.id, message: message(rule, ids, taken, value, credits, subjects) });
    }

    for (const corequisite of offering.corequisites) {
        if (!chosen.has(corequisite.moduleID) || chosen.has(corequisite.requiredModuleID)) continue;
        if (!subjects.has(corequisite.requiredModuleID)) continue;
        findings.push({
            kind: FindingKinds.Corequisite,
            ruleID: null,
            message: `${subjects.get(corequisite.moduleID)!.name} must be taken with ${subjects.get(corequisite.requiredModuleID)!.name}.`,
        });
    }
    return findings;
}

function message(
    rule: RuleDefinition,
    ids: string[],
    taken: string[],
    value: number,
    credits: boolean,
    subjects: Map<string, OfferedSubject>
): string {
    const name = (id: string) => subjects.get(id)!.name;
    if (credits) return `${rule.name}: needs ${bounds(rule.minimum, rule.maximum)} credits (${number(value)} credits chosen).`;
    if (rule.maximum === 1 && taken.length > 1)
        return taken.length === 2
            ? `${name(taken[0])} and ${name(taken[1])} cannot both be taken.`
            : `Only one of ${join(taken.map(name))} may be taken.`;
    if (rule.minimum === ids.length && rule.maximum === ids.length && taken.length < ids.length)
        return ids.length === 1 ? `${name(ids[0])} is compulsory.` : `${rule.name}: ${join(ids.map(name))} are all compulsory.`;
    return `${rule.name}: choose ${bounds(rule.minimum, rule.maximum)} (${number(value)} chosen).`;
}

// ------------------------------------------------------------------ helpers the screens share

/** A rule whose members must all be taken: Compulsory. */
export const isCompulsory = (rule: RuleDefinition, offering: Offering) => {
    const ids = members(rule, offering.subjects);
    return !isCredits(rule) && ids.length > 0 && rule.minimum === ids.length;
};

/** Exactly one of the members: shown as radio buttons, and ticking one unticks the others. */
export const isExactlyOne = (rule: RuleDefinition) => !isCredits(rule) && !rule.appliesToAllSubjects && rule.minimum === 1 && rule.maximum === 1;

export const membersOf = members;

export const isCreditRule = isCredits;

/** Plain-English sentence for a rule, for the rules list and the grid's band labels. */
export function describeRule(rule: RuleDefinition, subjectName: (id: string) => string): string {
    const scope = rule.appliesToAllSubjects ? "all subjects" : join((rule.moduleIDs ?? []).map(subjectName));
    const unit = isCredits(rule) ? " credits" : "";
    if (!isCredits(rule) && !rule.appliesToAllSubjects && rule.minimum === rule.moduleIDs.length && rule.maximum === rule.moduleIDs.length)
        return rule.moduleIDs.length === 1 ? `${scope} is compulsory.` : `${scope} are all compulsory.`;
    return `Choose ${bounds(rule.minimum, rule.maximum)}${unit} from ${scope}.`;
}
